import { InputHTMLAttributes, forwardRef, ReactNode } from 'react'

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix' | 'suffix'> {
  label?: string
  hint?: string
  error?: string
  example?: string
  prefix?: ReactNode
  suffix?: ReactNode
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, hint, error, example, prefix, suffix, className = '', id, ...rest }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, '-')
    return (
      <>
        <div className={`field ${error ? 'field--error' : ''} ${className}`}>
          {label && (
            <label className="field-label" htmlFor={inputId}>{label}</label>
          )}
          {example && (
            <p className="field-example">e.g. {example}</p>
          )}
          <div className={`input-wrap ${prefix ? 'input-wrap--prefix' : ''} ${suffix ? 'input-wrap--suffix' : ''}`}>
            {prefix && <span className="input-affix input-prefix">{prefix}</span>}
            <input ref={ref} id={inputId} className="input" {...rest} />
            {suffix && <span className="input-affix input-suffix">{suffix}</span>}
          </div>
          {hint && !error && <p className="field-hint">{hint}</p>}
          {error && <p className="field-error-msg">{error}</p>}
        </div>

        <style>{`
          .field { display: flex; flex-direction: column; gap: 5px; }
          .field-label { font-size: 0.8rem; font-weight: 600; color: var(--text-secondary); letter-spacing: 0.02em; }
          .field-example { font-size: 0.75rem; color: var(--text-muted); font-style: italic; margin-top: -2px; }
          .input-wrap { position: relative; display: flex; align-items: center; }
          .input {
            width: 100%;
            padding: 8px 12px;
            background: var(--bg-input);
            border: 1px solid var(--border);
            border-radius: var(--radius-md);
            color: var(--text-primary);
            font-size: 0.875rem;
            transition: border-color 0.15s;
            outline: none;
          }
          .input:focus { border-color: var(--accent); }
          .input::placeholder { color: var(--text-muted); }
          .field--error .input { border-color: var(--color-error); }
          .input-wrap--prefix .input { padding-left: 36px; }
          .input-wrap--suffix .input { padding-right: 36px; }
          .input-affix {
            position: absolute;
            display: flex;
            align-items: center;
            justify-content: center;
            width: 36px;
            color: var(--text-muted);
            font-size: 0.85rem;
            pointer-events: none;
          }
          .input-prefix { left: 0; }
          .input-suffix { right: 0; }
          .field-hint { font-size: 0.75rem; color: var(--text-muted); }
          .field-error-msg { font-size: 0.75rem; color: var(--color-error); }
        `}</style>
      </>
    )
  }
)
Input.displayName = 'Input'
